import { useEffect, useState } from "react";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription,
  DialogFooter,
} from "../ui/dialog";
import { Button } from "../ui/button";
import { Input } from "../ui/input";
import { Label } from "../ui/label";
import { Switch } from "../ui/switch";
import { Loader2, RotateCcw, AlertTriangle, Clock, Pencil, EyeOff, Eye } from "lucide-react";
import { toast } from "sonner";
import api from "../../lib/api";

const fmtEUR = (n) => new Intl.NumberFormat("es-ES", {
  style: "currency", currency: "EUR",
}).format(n || 0);

function timeAgo(iso) {
  if (!iso) return "";
  const diff = (Date.now() - new Date(iso).getTime()) / 1000;
  if (diff < 60) return "hace unos segundos";
  if (diff < 3600) return `hace ${Math.floor(diff / 60)} min`;
  if (diff < 86400) return `hace ${Math.floor(diff / 3600)} h`;
  const d = Math.floor(diff / 86400);
  if (d < 30) return `hace ${d} día${d === 1 ? "" : "s"}`;
  return new Date(iso).toLocaleDateString("es-ES", {
    day: "2-digit", month: "short", year: "numeric",
  });
}

function HistoryRow({ entry }) {
  const before = entry.before || {};
  const after = entry.after || {};
  const diffs = [];
  if (before.disabled !== after.disabled) {
    diffs.push(after.disabled
      ? "desactivado"
      : (before.disabled ? "reactivado" : null));
  }
  if (before.custom_amount !== after.custom_amount) {
    const from = before.custom_amount != null ? fmtEUR(before.custom_amount) : "auto";
    const to = after.custom_amount != null ? fmtEUR(after.custom_amount) : "auto";
    diffs.push(`importe ${from} → ${to}`);
  }
  if (before.custom_label !== after.custom_label) {
    const from = before.custom_label || "auto";
    const to = after.custom_label || "auto";
    diffs.push(`nombre «${from}» → «${to}»`);
  }
  const summary = diffs.filter(Boolean).join(" · ") || entry.action;

  const icon = {
    edit: <Pencil className="w-3 h-3 text-indigo-600" />,
    disable: <EyeOff className="w-3 h-3 text-rose-700" />,
    enable: <Eye className="w-3 h-3 text-emerald-700" />,
    reset: <RotateCcw className="w-3 h-3 text-muted-foreground" />,
  }[entry.action] || <Clock className="w-3 h-3 text-muted-foreground" />;

  return (
    <li className="flex items-start gap-2 py-1.5 text-xs">
      <span className="mt-0.5 shrink-0">{icon}</span>
      <div className="flex-1 min-w-0">
        <div className="text-foreground">{summary}</div>
        <div className="text-[10px] text-muted-foreground">
          {entry.changed_by_email || "—"} · {timeAgo(entry.changed_at)}
        </div>
      </div>
    </li>
  );
}

/**
 * Dialog to edit, disable or reset a recurring expense pattern.
 *
 * Props:
 *   open, onOpenChange
 *   pattern               — the pattern object (key, supplier_name, amount,
 *                           detected_amount, cadence, has_override)
 *   restaurantId
 *   onSaved()             — called after any successful mutation
 */
export default function EditRecurringPatternDialog({
  open, onOpenChange, pattern, restaurantId, onSaved,
}) {
  const [label, setLabel] = useState("");
  const [amount, setAmount] = useState("");
  const [disabled, setDisabled] = useState(false);
  const [saving, setSaving] = useState(false);
  const [resetting, setResetting] = useState(false);
  const [history, setHistory] = useState([]);
  const [historyLoading, setHistoryLoading] = useState(false);

  useEffect(() => {
    if (!open || !pattern) return;
    const ov = pattern.override || {};
    setLabel(ov.custom_label || pattern.supplier_name || "");
    setAmount(
      ov.custom_amount != null
        ? String(ov.custom_amount)
        : String(pattern.detected_amount || pattern.amount || ""),
    );
    setDisabled(!!ov.disabled);
  }, [open, pattern]);

  useEffect(() => {
    if (!open || !pattern?.key || !restaurantId) return;
    setHistoryLoading(true);
    let cancelled = false;
    api.getRecurringOverrideHistory(restaurantId, pattern.key, 10)
      .then((r) => { if (!cancelled) setHistory(r.entries || []); })
      .catch(() => { if (!cancelled) setHistory([]); })
      .finally(() => { if (!cancelled) setHistoryLoading(false); });
    return () => { cancelled = true; };
  }, [open, pattern?.key, restaurantId]);

  if (!pattern) return null;

  const detected = pattern.detected_amount ?? pattern.amount;
  const amountChanged = Number(amount) && Math.abs(Number(amount) - detected) > 0.01;
  const labelChanged = label.trim() !== (pattern.supplier_name || "").trim()
    && !(pattern.override && pattern.override.custom_label === label.trim());

  const handleSave = async () => {
    setSaving(true);
    try {
      const body = {
        disabled,
        custom_amount: amountChanged ? Number(amount) : null,
        custom_label: labelChanged && label.trim() ? label.trim() : null,
      };
      await api.upsertRecurringOverride(restaurantId, pattern.key, body);
      toast.success(disabled ? "Patrón desactivado" : "Patrón actualizado");
      onSaved?.();
      onOpenChange(false);
    } catch (err) {
      toast.error(err.message || "No se pudo guardar");
    } finally {
      setSaving(false);
    }
  };

  const handleReset = async () => {
    setResetting(true);
    try {
      await api.deleteRecurringOverride(restaurantId, pattern.key);
      toast.success("Restablecido a detección automática");
      onSaved?.();
      onOpenChange(false);
    } catch (err) {
      toast.error(err.message || "No se pudo restablecer");
    } finally {
      setResetting(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-md" data-testid="edit-recurring-pattern-dialog">
        <DialogHeader>
          <DialogTitle>Editar pago recurrente</DialogTitle>
          <DialogDescription>
            Ajusta el nombre o el importe, o desactiva este patrón si ya no
            procede. Se reflejará al instante en la previsión de caja.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4 py-2">
          <div className="space-y-1.5">
            <Label htmlFor="pat-label" className="text-sm">Nombre a mostrar</Label>
            <Input
              id="pat-label"
              value={label}
              onChange={(e) => setLabel(e.target.value)}
              placeholder="Ej. Alquiler local"
              disabled={disabled}
              data-testid="pattern-label-input"
            />
            {pattern.override?.custom_label && (
              <div className="text-[11px] text-muted-foreground">
                Detección automática: «{pattern.supplier_name || "—"}»
              </div>
            )}
          </div>

          <div className="space-y-1.5">
            <Label htmlFor="pat-amount" className="text-sm">Importe (EUR)</Label>
            <Input
              id="pat-amount"
              type="number"
              step="0.01"
              min="0"
              value={amount}
              onChange={(e) => setAmount(e.target.value)}
              disabled={disabled}
              data-testid="pattern-amount-input"
            />
            <div className="text-[11px] text-muted-foreground">
              Detección automática:{" "}
              <b>{new Intl.NumberFormat("es-ES", {
                style: "currency", currency: "EUR",
              }).format(detected)}</b>
              {" · "}{pattern.cadence === "monthly" ? "mensual" : "trimestral"}
            </div>
          </div>

          <div className="flex items-start gap-3 p-3 border rounded-lg bg-muted/50/50">
            <Switch
              id="pat-disabled"
              checked={disabled}
              onCheckedChange={setDisabled}
              data-testid="pattern-disable-toggle"
            />
            <div className="flex-1 min-w-0">
              <Label htmlFor="pat-disabled" className="text-sm font-medium cursor-pointer">
                Desactivar este patrón
              </Label>
              <p className="text-[11px] text-muted-foreground mt-0.5">
                Dejará de proyectarse en la previsión de caja. Podrás reactivarlo
                después desde el panel de «Desactivados».
              </p>
            </div>
          </div>

          {disabled && (
            <div className="flex items-start gap-2 text-xs text-amber-800 bg-amber-50 border border-amber-200 rounded-md px-2.5 py-2">
              <AlertTriangle className="w-3.5 h-3.5 shrink-0 mt-0.5" />
              <span>
                Los gastos del histórico con este patrón volverán a repartirse
                en la media diaria de opex (comportamiento previo a la detección).
              </span>
            </div>
          )}

          {/* ── Audit history ─────────────────────────────────────────── */}
          {(historyLoading || history.length > 0) && (
            <div className="pt-2 border-t" data-testid="pattern-history-section">
              <div className="flex items-center gap-1.5 text-xs font-medium text-muted-foreground mb-1.5">
                <Clock className="w-3 h-3" />
                Historial de cambios
              </div>
              {historyLoading ? (
                <div className="flex items-center justify-center py-2">
                  <Loader2 className="w-3.5 h-3.5 animate-spin text-muted-foreground" />
                </div>
              ) : (
                <ul className="max-h-40 overflow-auto divide-y divide-slate-100 pr-1"
                    data-testid="pattern-history-list">
                  {history.map((h, idx) => (
                    <HistoryRow key={`${h.changed_at}-${idx}`} entry={h} />
                  ))}
                </ul>
              )}
            </div>
          )}
        </div>

        <DialogFooter className="flex items-center justify-between sm:justify-between gap-2">
          {pattern.has_override ? (
            <Button
              type="button"
              variant="ghost"
              size="sm"
              onClick={handleReset}
              disabled={saving || resetting}
              className="text-muted-foreground hover:text-foreground"
              data-testid="pattern-reset-btn"
            >
              {resetting ? <Loader2 className="w-3.5 h-3.5 animate-spin mr-1.5" />
                         : <RotateCcw className="w-3.5 h-3.5 mr-1.5" />}
              Restablecer automático
            </Button>
          ) : <span />}
          <div className="flex gap-2">
            <Button variant="outline" onClick={() => onOpenChange(false)} disabled={saving}>
              Cancelar
            </Button>
            <Button onClick={handleSave} disabled={saving} data-testid="pattern-save-btn">
              {saving && <Loader2 className="w-3.5 h-3.5 animate-spin mr-1.5" />}
              Guardar
            </Button>
          </div>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
